import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  Zap, 
  TrendingUp, 
  Users, 
  MessageSquare, 
  Shield,
  Sparkles,
  ChevronRight,
  BarChart3,
  Clock,
  Heart,
  Globe,
  Smartphone,
  Brain,
  Waves
} from 'lucide-react';

export default function PousadaMasteryLanding() {
  const navigate = useNavigate();
  const [currentMetric, setCurrentMetric] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMetric((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const metrics = [
    { value: '94%', label: 'Guest Satisfaction', icon: Heart, color: 'text-pink-500' },
    { value: '87%', label: 'Operational Efficiency', icon: Zap, color: 'text-yellow-500' },
    { value: '3.2x', label: 'Revenue Growth', icon: TrendingUp, color: 'text-green-500' },
    { value: '45min', label: 'Daily Time Saved', icon: Clock, color: 'text-blue-500' }
  ];

  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Intelligence',
      description: 'Predictive maintenance, smart inventory, and guest preference learning',
      link: '/pousada-ai'
    },
    {
      icon: MessageSquare,
      title: 'Omnichannel Communication',
      description: 'WhatsApp, Instagram, and voice - all unified in one platform',
      link: '/adapters'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Experience',
      description: 'Manage everything from your phone, anywhere, anytime',
      link: '/pousada-mobile'
    },
    {
      icon: Globe,
      title: 'Universal Integrations',
      description: 'Connect with Booking.com, Airbnb, IoT sensors, and more',
      link: '/adapters'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
          style={{
            top: '20%',
            left: '10%',
            transform: `translateY(${scrollY * 0.2}px)`
          }}
        />
        <div 
          className="absolute w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          style={{
            bottom: '20%',
            right: '10%',
            transform: `translateY(${-scrollY * 0.2}px)`
          }}
        />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 px-6 py-4 border-b border-white/10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Waves className="h-8 w-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">Pousada Mastery</span>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/setup')}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:opacity-90 transition-opacity font-semibold"
            >
              🔧 Methodical Setup
            </button>
            <button
              onClick={() => navigate('/dev')}
              className="px-4 py-2 bg-yellow-600/20 text-yellow-300 rounded-lg hover:bg-yellow-600/30 transition-colors"
            >
              Dev Dashboard
            </button>
            <button
              onClick={() => navigate('/whatsapp')}
              className="px-4 py-2 text-white/80 hover:text-white transition-colors"
            >
              WhatsApp
            </button>
            <button
              onClick={() => navigate('/operator-mastery')}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Operator Dashboard
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 backdrop-blur-sm rounded-full text-purple-300 text-sm mb-8">
            <Sparkles className="h-4 w-4" />
            <span>Transform your pousada with AI automation</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Master Your Pousada
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Delight Every Guest
            </span>
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
            From uncertainty to confidence. Empower your team, automate operations, 
            and create unforgettable experiences with intelligent hospitality management.
          </p>

          {/* Live Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div
                  key={index}
                  className={`bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 transform transition-all duration-500 ${
                    currentMetric === index ? 'scale-105 border-purple-400/50' : ''
                  }`}
                >
                  <Icon className={`h-8 w-8 ${metric.color} mb-3 mx-auto`} />
                  <div className="text-3xl font-bold text-white mb-1">{metric.value}</div>
                  <div className="text-sm text-gray-400">{metric.label}</div>
                </div>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/operator-mastery')}
              className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2"
            >
              Start Your Journey
              <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/pousada-mobile')}
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl text-lg font-semibold hover:bg-white/20 transition-all"
            >
              See Live Demo
            </button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-12">
            Everything You Need to Excel
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(feature.link)}
                  className="group bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-purple-400/50 transition-all hover:scale-105 text-left"
                >
                  <Icon className="h-10 w-10 text-purple-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                  <div className="mt-4 text-purple-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                    Explore
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Showcase */}
      <section className="relative z-10 px-6 py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                AI That Understands Hospitality
              </h2>
              <p className="text-gray-300 mb-8">
                Our AI doesn't just respond - it anticipates. From predicting when your 
                water tank needs attention to knowing exactly when to reorder coffee pods, 
                it's like having a seasoned manager who never sleeps.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Shield className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Predictive Maintenance</h4>
                    <p className="text-gray-400 text-sm">Prevent issues before they impact guests</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Users className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Guest Preference Learning</h4>
                    <p className="text-gray-400 text-sm">Personalize every stay automatically</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Revenue Optimization</h4>
                    <p className="text-gray-400 text-sm">Smart pricing and occupancy management</p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/pousada-ai')}
                className="mt-8 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                Explore AI Features
              </button>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <div className="space-y-4">
                  {/* Mock AI conversation */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-500/20 rounded-full">
                        <MessageSquare className="h-4 w-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Guest via WhatsApp</p>
                        <p className="text-white mt-1">Hi! Is the pool heated? And do you have vegan breakfast options?</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-500/20 rounded-full">
                        <Brain className="h-4 w-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">AI Response</p>
                        <p className="text-white mt-1">Hello! Yes, our pool is heated to 28°C. We have several vegan options including tropical fruit bowls, avocado toast, and homemade granola with coconut milk. Shall I note your preference for tomorrow's breakfast? 🌱</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
                    <div className="flex items-start gap-3">
                      <Zap className="h-5 w-5 text-yellow-400 mt-0.5" />
                      <div className="text-sm">
                        <p className="text-yellow-300 font-semibold">AI Action Triggered</p>
                        <p className="text-gray-300 mt-1">Guest dietary preference saved. Kitchen notified for breakfast prep.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative z-10 px-6 py-20 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Transform Your Pousada?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join the future of hospitality management. Start your mastery journey today.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/operator-mastery')}
              className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2"
            >
              <Home className="h-5 w-5" />
              Operator Dashboard
              <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/mastery')}
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl text-lg font-semibold hover:bg-white/20 transition-all flex items-center gap-2"
            >
              <Smartphone className="h-5 w-5" />
              Guest Experience
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
          <div className="flex items-center gap-2 mb-4 md:mb-0">
            <Waves className="h-5 w-5 text-purple-400" />
            <span>Pousada Mastery - Powered by biig.biz</span>
          </div>
          <div className="flex gap-6">
            <button onClick={() => navigate('/setup')} className="hover:text-white transition-colors font-semibold">
              🔧 Methodical Setup
            </button>
            <button onClick={() => navigate('/dev')} className="hover:text-white transition-colors">
              Dev Dashboard
            </button>
            <button onClick={() => navigate('/whatsapp')} className="hover:text-white transition-colors">
              WhatsApp
            </button>
            <button onClick={() => navigate('/pousada-ops')} className="hover:text-white transition-colors">
              Operations
            </button>
            <button onClick={() => navigate('/adapters')} className="hover:text-white transition-colors">
              Integrations
            </button>
            <button onClick={() => navigate('/pousada-ai')} className="hover:text-white transition-colors">
              AI Engine
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}